import React, { useEffect, useState } from "react";
import { Card, Stat, StatusBadge } from "@/components/UI";
import { Coins, Trophy, ChartLine } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { CHART } from "./helpers";


export function StatsBlock({ title, data, testId }) {
  const ins = data.inseminations;
  const preg = data.pregnancy;
  const treat = data.treatments;
  const birth = data.births;
  return (
    <section data-testid={testId} className="space-y-6">
      <h3 className="heading text-2xl">{title}</h3>

      {/* Insemination */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">Tohumlama</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Tohumlanan Hayvan" value={ins.total_animals ?? ins.total ?? 0} unit="baş" tone="accent" />
          <Stat label="Kayıt Sayısı" value={ins.total_records ?? "—"} unit="adet" />
          <Stat label="İnek" value={(ins.by_animal_type?.find(x => x.type === "inek")?.count) ?? 0} unit="baş" tone="ok" />
          <Stat label="Düve" value={(ins.by_animal_type?.find(x => x.type === "düve")?.count) ?? 0} unit="baş" />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <Stat label="Dişi Tohum" value={(ins.by_semen_type?.find(x => x.type === "dişi")?.count) ?? 0} unit={`baş (%${(ins.by_semen_type?.find(x => x.type === "dişi")?.pct) ?? 0})`} />
          <Stat label="Konvansiyonel" value={(ins.by_semen_type?.find(x => x.type === "konvansiyonel")?.count) ?? 0} unit={`baş (%${(ins.by_semen_type?.find(x => x.type === "konvansiyonel")?.pct) ?? 0})`} />
          <Stat label="Erkek Tohum" value={(ins.by_semen_type?.find(x => x.type === "erkek")?.count) ?? 0} unit={`baş (%${(ins.by_semen_type?.find(x => x.type === "erkek")?.pct) ?? 0})`} />
        </div>
        {ins.by_technician?.length > 0 && (
          <div className="mt-3 text-sm">
            <span className="text-[#6b746c]">En aktif tohumlayanlar:</span>{" "}
            {ins.by_technician.slice(0, 3).map(t => `${t.technician} (${t.count})`).join(" · ")}
          </div>
        )}
      </div>

      {/* Pregnancy */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">Gebelik Kontrolü</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Bakılan" value={preg.checked_total ?? 0} unit="baş" tone="accent" />
          <Stat label="Gebe (+)" value={preg.pregnant_total ?? 0} unit="baş" tone="ok" />
          <Stat label="Boş (GD-)" value={preg.empty_total ?? 0} unit="baş" tone="warn" />
          <Stat label="Gebelik Oranı" value={preg.pregnancy_rate ?? 0} unit="%" tone="ok" />
          <Stat label="Boş Oranı" value={preg.empty_rate ?? 0} unit="%" tone="warn" />
        </div>
        {preg.by_animal_type?.some(x => x.checked > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {preg.by_animal_type.filter(x => x.checked > 0).map(x => (
              <div key={x.type} className="card-flat p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="capitalize font-semibold">{x.type}</span>
                  <span className="text-sm text-[#5e675e]">{x.checked} baş bakıldı</span>
                </div>
                <div className="text-sm text-[#5e675e]">
                  <b className="text-[#2D4A36]">{x.pregnant}</b> gebe · <b className="text-[#C45B45]">{x.empty}</b> boş · Oran: <b>%{x.pregnancy_rate}</b>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Births */}
      {birth && (
        <div>
          <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">Doğum</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Toplam Yavru" value={birth.calf_count ?? 0} unit="adet" tone="accent" />
            <Stat label="Canlı Doğan" value={birth.live_count ?? 0} unit="adet" tone="ok" />
            <Stat label="Ölü Doğan" value={birth.stillborn_count ?? 0} unit="adet" tone="warn" />
            <Stat label="Güç Doğum" value={(birth.difficult_count ?? 0) + (birth.cesarean_count ?? 0)} unit={`adet (%${birth.difficult_rate ?? 0})`} tone="warn" />
          </div>
          {birth.live_count > 0 && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="card-flat p-4">
                <div className="text-xs text-[#6b746c] mb-1">Dişi Yavru</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-[#C45B45] tabnum">{birth.female_count ?? 0}</span>
                  <span className="text-sm text-[#5e675e]">baş · %{birth.female_pct ?? 0}</span>
                </div>
              </div>
              <div className="card-flat p-4">
                <div className="text-xs text-[#6b746c] mb-1">Erkek Yavru</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-[#2D4A36] tabnum">{birth.male_count ?? 0}</span>
                  <span className="text-sm text-[#5e675e]">baş · %{birth.male_pct ?? 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Treatments */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">Tedavi</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <Stat label="Toplam Tedavi" value={treat.total} unit="adet" tone="accent" />
          <Stat label="İyileşme Oranı" value={treat.recovery_rate} unit="%" tone="ok" />
          <Stat label="Toplam İlaç Gideri" value={treat.medicine_expense.toLocaleString("tr-TR")} unit="₺" tone="warn" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">Hayvan Kategorilerine Göre</div>
            <div style={{ height: 180 }}>
              <ResponsiveContainer>
                <BarChart data={treat.by_category}>
                  <XAxis dataKey="category" tick={{ fontSize: 11, fill: "#8a938c" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#8a938c" }} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} />
                  <Bar dataKey="count" fill="#2D4A36" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-4 gap-2 text-sm mt-2">
              {treat.by_category.map(c => (
                <div key={c.category} className="text-center">
                  <div className="text-xs text-[#6b746c] capitalize">{c.category}</div>
                  <div className="font-semibold tabnum">{c.count}</div>
                  <div className="text-xs text-[#8a938c]">{c.pct}%</div>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">En Çok Kullanılan İlaçlar</div>
            {treat.top_medicines.length ? (
              <div style={{ height: 180 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={treat.top_medicines} dataKey="count" nameKey="name" innerRadius={40} outerRadius={75} paddingAngle={2}>
                      {treat.top_medicines.map((m, i) => <Cell key={m.name} fill={CHART[i % CHART.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="text-sm text-[#8a938c] py-8 text-center">Kayıt yok</div>}
            <div className="mt-2 space-y-1 text-sm">
              {treat.top_medicines.slice(0, 5).map((m, i) => (
                <div key={m.name} className="flex justify-between">
                  <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: CHART[i % CHART.length] }} />{m.name}</span>
                  <span className="tabnum">{m.count}x</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
        {treat.top_diagnoses.length > 0 && (
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <Card>
              <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">Genel En Sık Teşhisler</div>
              <ul className="space-y-1.5 text-sm">
                {treat.top_diagnoses.slice(0, 10).map((d, i) => (
                  <li key={d.diagnosis} className="flex justify-between border-b border-[#EEECE5] pb-1">
                    <span><span className="inline-block w-5 text-[#8a938c] text-xs">#{i+1}</span> {d.diagnosis}</span>
                    <span className="tabnum font-semibold">{d.count}x</span>
                  </li>
                ))}
              </ul>
            </Card>
            <Card>
              <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">Hayvan Tipine Göre En Sık Teşhisler</div>
              <div className="space-y-3">
                {Object.entries(treat.top_diagnoses_by_type || {}).map(([type, diags]) => (
                  diags.length > 0 && (
                    <div key={type}>
                      <div className="text-sm font-semibold capitalize mb-1">{type}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {diags.slice(0, 4).map(d => (
                          <span key={d.diagnosis} className="text-xs px-2 py-1 rounded-md bg-[#F0EEE8] border border-[#E5E2DC]">
                            {d.diagnosis} <b className="text-[#2D4A36]">{d.count}x</b>
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                ))}
                {!Object.values(treat.top_diagnoses_by_type || {}).some(v => v.length > 0) && (
                  <div className="text-sm text-[#8a938c]">Bu periyotta teşhis kaydı yok</div>
                )}
              </div>
            </Card>
          </div>
        )}

        {treat.top_diagnoses_by_cost?.length > 0 && (
          <Card className="mt-4" testId="top-cost-diagnoses">
            <div className="flex items-center gap-2 mb-1">
              <Coins size={18} weight="fill" color="#C45B45" />
              <div className="text-xs uppercase tracking-widest text-[#6b746c]">En Pahalı Teşhisler (İlaç Maliyeti)</div>
            </div>
            <p className="text-xs text-[#5e675e] mb-3">Bu teşhisler işletmenize en çok ilaç gideri yaratıyor — koruyucu sağlık yatırımı için öncelik listeniz.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <table className="data-table !p-0">
                <thead><tr><th>Sıra</th><th>Teşhis</th><th className="text-right">Vaka</th><th className="text-right">Toplam</th><th className="text-right">Vaka Başı</th></tr></thead>
                <tbody>
                  {treat.top_diagnoses_by_cost.map((d, i) => (
                    <tr key={d.diagnosis}>
                      <td className="tabnum text-[#8a938c] text-sm">#{i+1}</td>
                      <td className="font-semibold">{d.diagnosis}</td>
                      <td className="text-right tabnum">{d.count}</td>
                      <td className="text-right tabnum font-semibold text-[#C45B45]">{Number(d.cost).toLocaleString("tr-TR")} ₺</td>
                      <td className="text-right tabnum text-sm text-[#5e675e]">{Number(d.avg_cost).toLocaleString("tr-TR")} ₺</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ height: 200 }}>
                <ResponsiveContainer>
                  <BarChart data={treat.top_diagnoses_by_cost} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#8a938c" }} />
                    <YAxis type="category" dataKey="diagnosis" tick={{ fontSize: 11, fill: "#5e675e" }} width={100} />
                    <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} formatter={(v) => `${Number(v).toLocaleString("tr-TR")} ₺`} />
                    <Bar dataKey="cost" fill="#C45B45" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        )}
      </div>
    </section>
  );
}

